/**
 * Serviço de Canvas Fingerprinting
 * Responsável por gerar fingerprints únicos usando Canvas 2D
 */

import { CanvasFactory } from '../utils/CanvasFactory.js';

export class CanvasService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('CanvasService');
        this.cache = new Map();
        this.stats = {
            calls: 0,
            errors: 0,
            cacheHits: 0,
            avgExecutionTime: 0
        };
    }

    /**
     * Obtém fingerprint do canvas
     */
    async getFingerprint() {
        if (!this.config.canvasEnabled) {
            this.logger.debug('Canvas fingerprinting desabilitado');
            return { enabled: false };
        }

        const startTime = performance.now();
        this.stats.calls++;

        try {
            // Verifica cache primeiro
            const cacheKey = 'canvas_fingerprint';
            if (this.cache.has(cacheKey)) {
                this.stats.cacheHits++;
                this.logger.debug('Canvas fingerprint obtido do cache');
                return this.cache.get(cacheKey);
            }

            const fingerprint = await this.generateCanvasFingerprint();

            // Armazena no cache
            this.cache.set(cacheKey, fingerprint);

            const executionTime = performance.now() - startTime;
            this.updateStats(executionTime);

            this.logger.debug('Canvas fingerprint gerado', {
                executionTime: `${executionTime.toFixed(2)}ms`,
                dataUrl: fingerprint.data_url?.substring(0, 50) + '...'
            });

            return fingerprint;

        } catch (error) {
            this.stats.errors++;
            this.logger.error('Erro ao gerar canvas fingerprint', { error: error.message });
            throw error;
        }
    }

    /**
     * Gera fingerprint do canvas com múltiplas técnicas
     */
    async generateCanvasFingerprint() {
        const canvas = CanvasFactory.createCanvas(280, 60);
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Canvas 2D context não disponível');
        }

        // Configuração do canvas
        canvas.width = 280;
        canvas.height = 60;

        // Técnica 1: Texto com diferentes fontes e estilos
        await this.drawTextFingerprint(ctx, canvas);

        // Técnica 2: Formas geométricas complexas
        this.drawGeometricShapes(ctx);

        // Técnica 3: Gradientes e padrões
        this.drawGradientsAndPatterns(ctx);

        // Técnica 4: Transformações e composições
        this.drawTransformations(ctx);

        // Obtém dados do canvas
        const dataUrl = canvas.toDataURL();
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        return {
            data_url: dataUrl,
            hash: await this.hashCanvasData(dataUrl),
            pixel_checksum: this.calculatePixelChecksum(imageData),
            dimensions: {
                width: canvas.width,
                height: canvas.height
            },
            context_attributes: this.getContextAttributes(ctx),
            techniques_used: ['text', 'geometry', 'gradients', 'transformations']
        };
    }

    /**
     * Desenha texto com diferentes características
     */
    async drawTextFingerprint(ctx, canvas) {
        // Fundo com gradiente
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#f0f0f0');
        gradient.addColorStop(0.5, '#e0e0e0');
        gradient.addColorStop(1, '#d0d0d0');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Texto principal com fonte específica
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#333';
        ctx.font = '14px Arial, sans-serif';
        ctx.fillText('Canvas Fingerprint 🔒', 4, 4);

        // Texto com fonte diferente
        ctx.font = 'bold 12px Times, serif';
        ctx.fillStyle = '#666';
        ctx.fillText('Security Test 123', 4, 20);

        // Texto com emoji e caracteres especiais
        ctx.font = '10px Courier, monospace';
        ctx.fillStyle = '#999';
        ctx.fillText('αβγδε ñáéíóú 中文 🌟', 4, 36);

        // Texto rotacionado
        ctx.save();
        ctx.translate(200, 30);
        ctx.rotate(Math.PI / 6);
        ctx.font = '11px Georgia, serif';
        ctx.fillStyle = '#c00';
        ctx.fillText('Rotated', 0, 0);
        ctx.restore();
    }

    /**
     * Desenha formas geométricas
     */
    drawGeometricShapes(ctx) {
        // Círculo com borda
        ctx.beginPath();
        ctx.arc(50, 30, 15, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
        ctx.fill();
        ctx.strokeStyle = '#f00';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Retângulo com cantos arredondados
        ctx.beginPath();
        ctx.roundRect(80, 15, 30, 20, 5);
        ctx.fillStyle = 'rgba(0, 255, 0, 0.5)';
        ctx.fill();
        ctx.strokeStyle = '#0f0';
        ctx.stroke();

        // Triângulo
        ctx.beginPath();
        ctx.moveTo(130, 35);
        ctx.lineTo(145, 15);
        ctx.lineTo(160, 35);
        ctx.closePath();
        ctx.fillStyle = 'rgba(0, 0, 255, 0.5)';
        ctx.fill();
        ctx.strokeStyle = '#00f';
        ctx.stroke();

        // Linha com padrão
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(170, 20);
        ctx.lineTo(250, 40);
        ctx.strokeStyle = '#f0f';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.setLineDash([]);
    }

    /**
     * Desenha gradientes e padrões
     */
    drawGradientsAndPatterns(ctx) {
        // Gradiente radial
        const radialGradient = ctx.createRadialGradient(200, 45, 0, 200, 45, 20);
        radialGradient.addColorStop(0, '#ff0');
        radialGradient.addColorStop(0.5, '#f80');
        radialGradient.addColorStop(1, '#f00');

        ctx.beginPath();
        ctx.arc(200, 45, 18, 0, 2 * Math.PI);
        ctx.fillStyle = radialGradient;
        ctx.fill();

        // Padrão com canvas pequeno
        const patternCanvas = CanvasFactory.createCanvas();
        patternCanvas.width = 10;
        patternCanvas.height = 10;
        const patternCtx = patternCanvas.getContext('2d');

        patternCtx.fillStyle = '#ccc';
        patternCtx.fillRect(0, 0, 5, 5);
        patternCtx.fillRect(5, 5, 5, 5);
        patternCtx.fillStyle = '#999';
        patternCtx.fillRect(5, 0, 5, 5);
        patternCtx.fillRect(0, 5, 5, 5);

        const pattern = ctx.createPattern(patternCanvas, 'repeat');
        ctx.fillStyle = pattern;
        ctx.fillRect(230, 25, 40, 20);
    }

    /**
     * Desenha com transformações
     */
    drawTransformations(ctx) {
        ctx.save();

        // Escala e rotação
        ctx.translate(260, 15);
        ctx.scale(0.8, 1.2);
        ctx.rotate(Math.PI / 8);

        // Retângulo transformado
        ctx.fillStyle = 'rgba(128, 0, 128, 0.7)';
        ctx.fillRect(-10, -5, 20, 10);

        ctx.restore();

        // Composição com blend mode
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
        ctx.fillRect(240, 35, 30, 15);
        ctx.globalCompositeOperation = 'source-over';
    }

    /**
     * Calcula hash dos dados do canvas
     */
    async hashCanvasData(dataUrl) {
        try {
            // Usa Web Crypto API se disponível
            if (crypto && crypto.subtle) {
                const encoder = new TextEncoder();
                const data = encoder.encode(dataUrl);
                const hashBuffer = await crypto.subtle.digest('SHA-256', data);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
        } catch (error) {
            this.logger.debug('Web Crypto API não disponível, usando hash simples');
        }

        // Fallback para hash simples
        return this.simpleHash(dataUrl);
    }

    /**
     * Hash simples para fallback
     */
    simpleHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Converte para 32bit
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Calcula checksum dos pixels
     */
    calculatePixelChecksum(imageData) {
        let checksum = 0;
        const data = imageData.data;

        // Amostra pixels em intervalos para performance
        for (let i = 0; i < data.length; i += 16) {
            checksum += data[i];
        }

        return checksum;
    }

    /**
     * Obtém atributos do contexto
     */
    getContextAttributes(ctx) {
        return {
            fillStyle: ctx.fillStyle,
            strokeStyle: ctx.strokeStyle,
            lineWidth: ctx.lineWidth,
            lineCap: ctx.lineCap,
            lineJoin: ctx.lineJoin,
            miterLimit: ctx.miterLimit,
            shadowBlur: ctx.shadowBlur,
            shadowColor: ctx.shadowColor,
            globalAlpha: ctx.globalAlpha,
            globalCompositeOperation: ctx.globalCompositeOperation,
            textAlign: ctx.textAlign,
            textBaseline: ctx.textBaseline
        };
    }

    /**
     * Atualiza estatísticas de performance
     */
    updateStats(executionTime) {
        const totalTime = this.stats.avgExecutionTime * (this.stats.calls - 1) + executionTime;
        this.stats.avgExecutionTime = totalTime / this.stats.calls;
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.cache.size,
            successRate: this.stats.calls > 0 ?
                ((this.stats.calls - this.stats.errors) / this.stats.calls * 100).toFixed(2) + '%' : '0%'
        };
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do CanvasService limpo');
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        if (!newConfig.canvasEnabled) {
            this.clearCache();
        }
        this.logger.debug('Configuração do CanvasService atualizada');
    }

    /**
     * Testa suporte ao Canvas
     */
    static isSupported() {
        try {
            const canvas = CanvasFactory.createCanvas();
            const ctx = canvas.getContext('2d');
            return !!(ctx && typeof ctx.fillText === 'function');
        } catch {
            return false;
        }
    }

    /**
     * Obtém capacidades do Canvas
     */
    static getCapabilities() {
        if (!CanvasService.isSupported()) {
            return { supported: false };
        }

        const canvas = CanvasFactory.createCanvas();
        const ctx = canvas.getContext('2d');

        return {
            supported: true,
            maxCanvasSize: CanvasService.getMaxCanvasSize(),
            supportedFonts: CanvasService.getSupportedFonts(),
            hasImageSmoothingEnabled: 'imageSmoothingEnabled' in ctx,
            hasFilter: 'filter' in ctx,
            hasPath2D: typeof Path2D !== 'undefined'
        };
    }

    /**
     * Obtém tamanho máximo do canvas
     */
    static getMaxCanvasSize() {
        const canvas = CanvasFactory.createCanvas();
        const ctx = canvas.getContext('2d');

        let maxSize = 32767; // Valor padrão conservador

        try {
            // Testa tamanhos crescentes
            for (let size = 1000; size <= 65536; size *= 2) {
                canvas.width = size;
                canvas.height = size;

                ctx.fillStyle = '#000';
                ctx.fillRect(0, 0, 1, 1);

                const imageData = ctx.getImageData(0, 0, 1, 1);
                if (imageData.data[3] !== 255) {
                    break;
                }

                maxSize = size;
            }
        } catch {
            // Mantém valor padrão
        }

        return maxSize;
    }

    /**
     * Obtém fontes suportadas
     */
    static getSupportedFonts() {
        const testFonts = [
            'Arial', 'Times', 'Courier', 'Georgia', 'Verdana',
            'Helvetica', 'Comic Sans MS', 'Impact', 'Trebuchet MS'
        ];

        const canvas = CanvasFactory.createCanvas();
        const ctx = canvas.getContext('2d');

        const supportedFonts = [];

        testFonts.forEach(font => {
            try {
                ctx.font = `12px ${font}`;
                if (ctx.font.includes(font)) {
                    supportedFonts.push(font);
                }
            } catch {
                // Fonte não suportada
            }
        });

        return supportedFonts;
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();
        this.logger.debug('CanvasService destruído');
    }
}